import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {DeviceService} from '../../services/device/device.service';
import {DeviceSlotId, ProjectId} from '../../types/ids';
import {DeployableService} from '../../services/device/deployable.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AssetAndBlobService} from '../../services/assetAndBlob.service';
import {SharedData} from '../../utils/shared-data.share';
import {Browser} from '../../types/browser';
import {Resolution} from '../../types/resolution';
import {WmNotificationService} from '../notification/wm-notification.service';

import {
    AdvertisedDeployable, anyAvailable,
    constructRequirementsForDeployableInSlot,
    Deployable, DeployableGroup, isAvailable, isDefect
} from '@webmate-web-core/shared/types/devices/device-template';
import {DevicePropertyNames} from "@webmate-web-core/shared/types/devices/device-properties";
import {LogoResolverService} from '@webmate-web-core/shared/services/logo-resolver.service';
import {capitalize} from '@webmate-web-core/shared/utils/utils';
import {WorkbenchOpenService} from '@webmate-web-core/shared/workbench-ipc';
import {take} from 'rxjs/operators';


@Component({
    selector: 'wm-deployable-group',
    templateUrl: './deployable-group.component.html',
    styleUrls: ['./deployable-group.component.less']
})
export class DeployableGroupComponent implements OnInit, OnChanges {

    @Input() deployableGroup: DeployableGroup;
    @Input() slotId: DeviceSlotId;
    @Input() projectId: ProjectId;

    @Input() viewType: string;

    available: boolean = false;

    advertisedDeployable: AdvertisedDeployable = undefined;
    browsers: Array<Browser> = undefined;

    manufacturer: string = undefined;

    deployed: number = 0;
    requestInProgress: boolean = false;
    deploymentDialogVisible: boolean = false;

    desiredName: string = "";
    allowJobs: boolean = false;

    supportsResolution: boolean = false;
    desiredResolution: Resolution = new Resolution(1920, 1080);
    availableResolutions: any[] = [
        new Resolution(1280, 800).toDropdownModel(),
        this.desiredResolution.toDropdownModel(),
        new Resolution(2560, 1440).toDropdownModel()
    ];

    subselectedDeployable: Deployable = undefined;
    // buffer browsers to display
    subselectedBrowsersToDisplay: Browser[] = [];

    deployButtonModel = [{label: 'Deploy and Open', command: () => this.requestDeploymentOfTemplate(true)}];

    constructor(private deployableService: DeployableService, private assetAndBlobService: AssetAndBlobService, private sharedData: SharedData,
                private deviceService: DeviceService, private router: Router, private route: ActivatedRoute, private platformLogoResolver: LogoResolverService,
                private notificationService: WmNotificationService, private readonly workbenchOpenService: WorkbenchOpenService) {}

    ngOnInit() {
        this.browsers = ([] as Browser[]).concat(...this.deployableGroup.deployables.map(d => d.browser));
        this.platformLogoResolver.getLogosForDeployables(this.deployableGroup.deployables.slice(0, 1)).subscribe(d => {
            this.advertisedDeployable = d[0];
            this.manufacturer = this.deployableGroup.deployables[0].properties[DevicePropertyNames.ManufacturerName];
        });
        this.sharedData.getData("selected-deployablegroup").subscribe( next => { if (next !== this.deployableGroup.id) this.deploymentDialogVisible = false; });
        this.subselectedDeployable = this.deployableGroup.deployables[0];
        this.subselectedBrowsersToDisplay = this.subselectedDeployable.browser.slice(0, 2);
        this.available = anyAvailable(this.deployableGroup);
        this.updateResolutionDropdownVisibility();
    }

    ngOnChanges() {
        this.updateResolutionDropdownVisibility();
    }

    getLocale(d: Deployable) {
        return d.properties[DevicePropertyNames.Locale];
    }

    private updateResolutionDropdownVisibility() {
        this.supportsResolution = !!this.deployableGroup.deployables.find(d => !!d.properties[DevicePropertyNames.ChangeResolution]);
    }

    requestDeploymentOfTemplate(openDevice: boolean) {
        this.requestInProgress = true;
        const name = (this.desiredName !== undefined && this.desiredName.trim().length > 0) ? this.desiredName.trim() : this.advertisedDeployable.displayName;
        let requirements = constructRequirementsForDeployableInSlot(this.deployableGroup.deployables.find(d => d.id === this.subselectedDeployable.id), this.slotId, this.allowJobs, this.supportsResolution ? this.desiredResolution : undefined);
        this.deviceService.requestDeployment$(this.projectId, requirements, name).subscribe(
            (device) => {
                if (openDevice) {
                    this.workbenchOpenService.openWorkbench({
                        projectId: this.projectId,
                        deviceId: device.id,
                    }, {
                        blockedPopupMessage: 'Your browser has blocked this console, please allow pop-ups / new tabs for this webpage!'
                    }).pipe(take(1)).subscribe();
                }
                this.requestInProgress = false;
                this.router.navigate(['..'], {relativeTo: this.route});
            },
            err => {
                const msg = "Request for deployment failed, error was: " + ((typeof(err['error']) === 'string' ? err['error'] : undefined) || 'unknown');
                console.error(msg, err);
                this.requestInProgress = false;
                this.notificationService.toastWarningNotification(msg);
            }
        );
    }

    toggleDeploymentWindow() {
        if (this.available) {
            if (this.viewType !== 'GRID') {
                this.deploymentDialogVisible = !this.deploymentDialogVisible;
            } else {
                this.deploymentDialogVisible = true;
            }

            if (this.deploymentDialogVisible) {
                this.sharedData.storeData("selected-deployablegroup", this.deployableGroup.id);
            }
        }
    }

    selectDeployable(depl: Deployable) {
        if (isAvailable(depl)) {
            this.subselectedDeployable = depl;
            this.subselectedBrowsersToDisplay = this.subselectedDeployable.browser.slice(0, 2);
        }
    }

    isDeployableAvailable(depl: Deployable): boolean {
        return isAvailable(depl) && !isDefect(depl);
    }

    hasAnnotations(d: Deployable) {
        return !!d.properties[DevicePropertyNames.Annotations];
    }

    getAnnotationStatus(d: Deployable) {
        return d.properties[DevicePropertyNames.Annotations]['status'];
    }

    getAnnotationString(d: Deployable) {
        let msg = d.properties[DevicePropertyNames.Annotations]['msg'];
        return capitalize(this.getAnnotationStatus(d)) + (!!msg ? ': ' + msg : '');
    }

    hasUserAnnotations(d: Deployable) {
        return !!d.properties[DevicePropertyNames.UserAnnotations];
    }

    getUserAnnotationString(d: Deployable) {
        return d.properties[DevicePropertyNames.UserAnnotations]['msg'];
    }


}
